import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { FirebaseError } from "firebase/app";
import type { User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { ZodError } from "zod";
import { db, functions } from "@/core/firebase/client";
import { ROOT_CLUBS, clubSubcollections } from "@/core/firebase/paths";
import { ClubSchema, type Club } from "@/core/models/club";
import { StaffMemberSchema, type StaffMember } from "@/core/models/staff";
import { logger } from "@/core/logger";
import { platformAdminRepository } from "@/core/auth/platform-admin-repository";

export interface UserClubMembership {
  club: Club;
  staff: StaffMember | null;
  /** True when the membership comes from platform-admin access, not staff doc. */
  isPlatformAdmin: boolean;
}

/**
 * Resolve every club the signed-in user has access to.
 *
 * 1. If the user is a platform admin → return ALL clubs (admin tooling).
 * 2. Otherwise → ask the server for active membership IDs, then read only
 *    those club docs through member-scoped Firestore rules.
 *
 * Pending staff invites are linked by the `complete_staff_invite` Cloud
 * Function during the accept-invite flow — admin SDK creates `staff/{uid}`
 * keyed by the invitee's UID. No client-side linking needed.
 *
 * The server scan remains acceptable while the platform is small (< 100
 * clubs); a later optimisation can use `collectionGroup('staff')` + index.
 */
class UserClubsRepository {
  async listForUser(user: User): Promise<UserClubMembership[]> {
    const isAdmin = await platformAdminRepository.isAdmin(user.email);
    if (isAdmin) {
      let clubsSnap;
      try {
        clubsSnap = await getDocs(collection(db, ROOT_CLUBS));
      } catch (e) {
        logger.e("user-clubs: admin club list failed", e);
        return [];
      }
      logger.i(
        `user-clubs: platform admin access for ${user.email} — listing all clubs`,
      );
      return clubsSnap.docs
        .map((d) => this.#parseClub(d.id, d.data()))
        .filter((c): c is Club => c !== null)
        .map(
          (club) =>
            ({
              club,
              staff: null,
              isPlatformAdmin: true,
            }) satisfies UserClubMembership,
        )
        .sort((a, b) => a.club.name.localeCompare(b.club.name));
    }

    let clubIds: string[];
    try {
      const list = httpsCallable<Record<string, never>, { clubIds: string[] }>(functions, 'list_my_club_ids');
      clubIds = (await list({})).data.clubIds;
    } catch (e) {
      const preview = e instanceof FirebaseError && e.code === 'functions/unauthenticated';
      if (preview) logger.d('user-clubs: preview session has no staff memberships', e);
      else logger.e('user-clubs: membership lookup failed', e);
      return [];
    }

    const memberships: UserClubMembership[] = [];
    await Promise.all(
      clubIds.map(async (clubId) => {
        const clubDoc = await getDoc(doc(db, ROOT_CLUBS, clubId)).catch((e) => {
          logger.d(`user-clubs: club ${clubId} read rejected`, e);
          return null;
        });
        const clubData = clubDoc?.data();
        if (!clubData) return;

        let staffSnap;
        try {
          staffSnap = await getDocs(
            query(
              collection(db, clubSubcollections.staff(clubId)),
              where("uid", "==", user.uid),
              limit(1),
            ),
          );
        } catch (e) {
          logger.d(
            `user-clubs: staff query rejected for ${clubId} (not a member)`,
            e,
          );
          return;
        }
        const staffDoc = staffSnap.docs[0];
        if (!staffDoc) return;

        let staff: StaffMember;
        let club: Club;
        try {
          staff = StaffMemberSchema.parse({
            id: staffDoc.id,
            ...staffDoc.data(),
          });
          club = ClubSchema.parse({ id: clubId, ...clubData });
        } catch (e) {
          if (e instanceof ZodError) {
            logger.e(`user-clubs: parse failed for club ${clubId}`, e.issues);
          }
          return;
        }

        memberships.push({ club, staff, isPlatformAdmin: false });
      }),
    );

    memberships.sort((a, b) => a.club.name.localeCompare(b.club.name));
    return memberships;
  }

  #parseClub(id: string, data: unknown): Club | null {
    try {
      return ClubSchema.parse({ id, ...(data as Record<string, unknown>) });
    } catch (e) {
      if (e instanceof ZodError) {
        logger.e(`user-clubs: parse failed for club ${id}`, e.issues);
      }
      return null;
    }
  }
}

export const userClubsRepository = new UserClubsRepository();
